/**
 * Form-respondent UI translations.
 *
 * Each entry maps an internal string key to the localized label shown
 * inside FormView (Next / Previous / Submit / validation messages, etc.).
 * If a translation is missing for a given language, the helper falls
 * back to English so the form still renders.
 *
 * `LANGUAGES` is the master list (code + native + english label + rtl flag)
 * used to populate the picker in FormBuilder > Form Settings.
 */

export const LANGUAGES = [
  { code: 'en',     name: 'English',                native: 'English',           rtl: false },
  { code: 'es',     name: 'Spanish',                native: 'Español',           rtl: false },
  { code: 'fr',     name: 'French',                 native: 'Français',          rtl: false },
  { code: 'de',     name: 'German',                 native: 'Deutsch',           rtl: false },
  { code: 'it',     name: 'Italian',                native: 'Italiano',          rtl: false },
  { code: 'pt',     name: 'Portuguese',             native: 'Português',         rtl: false },
  { code: 'sq',     name: 'Albanian',               native: 'Shqip',             rtl: false },
  { code: 'nl',     name: 'Dutch',                  native: 'Nederlands',        rtl: false },
  { code: 'sv',     name: 'Swedish',                native: 'Svenska',           rtl: false },
  { code: 'no',     name: 'Norwegian',              native: 'Norsk',             rtl: false },
  { code: 'da',     name: 'Danish',                 native: 'Dansk',             rtl: false },
  { code: 'fi',     name: 'Finnish',                native: 'Suomi',             rtl: false },
  { code: 'is',     name: 'Icelandic',              native: 'Íslenska',          rtl: false },
  { code: 'pl',     name: 'Polish',                 native: 'Polski',            rtl: false },
  { code: 'cs',     name: 'Czech',                  native: 'Čeština',           rtl: false },
  { code: 'sk',     name: 'Slovak',                 native: 'Slovenčina',        rtl: false },
  { code: 'hu',     name: 'Hungarian',              native: 'Magyar',            rtl: false },
  { code: 'ro',     name: 'Romanian',               native: 'Română',            rtl: false },
  { code: 'bg',     name: 'Bulgarian',              native: 'Български',         rtl: false },
  { code: 'el',     name: 'Greek',                  native: 'Ελληνικά',          rtl: false },
  { code: 'hr',     name: 'Croatian',               native: 'Hrvatski',          rtl: false },
  { code: 'sr',     name: 'Serbian',                native: 'Српски',            rtl: false },
  { code: 'sl',     name: 'Slovenian',              native: 'Slovenščina',       rtl: false },
  { code: 'mk',     name: 'Macedonian',             native: 'Македонски',        rtl: false },
  { code: 'lt',     name: 'Lithuanian',             native: 'Lietuvių',          rtl: false },
  { code: 'lv',     name: 'Latvian',                native: 'Latviešu',          rtl: false },
  { code: 'et',     name: 'Estonian',               native: 'Eesti',             rtl: false },
  { code: 'ru',     name: 'Russian',                native: 'Русский',           rtl: false },
  { code: 'uk',     name: 'Ukrainian',              native: 'Українська',        rtl: false },
  { code: 'be',     name: 'Belarusian',             native: 'Беларуская',        rtl: false },
  { code: 'tr',     name: 'Turkish',                native: 'Türkçe',            rtl: false },
  { code: 'az',     name: 'Azerbaijani',            native: 'Azərbaycanca',      rtl: false },
  { code: 'kk',     name: 'Kazakh',                 native: 'Қазақша',           rtl: false },
  { code: 'ky',     name: 'Kyrgyz',                 native: 'Кыргызча',          rtl: false },
  { code: 'uz',     name: 'Uzbek',                  native: 'Oʻzbekcha',         rtl: false },
  { code: 'hy',     name: 'Armenian',               native: 'Հայերեն',           rtl: false },
  { code: 'ka',     name: 'Georgian',               native: 'ქართული',          rtl: false },
  { code: 'ar',     name: 'Arabic',                 native: 'العربية',           rtl: true  },
  { code: 'he',     name: 'Hebrew',                 native: 'עברית',             rtl: true  },
  { code: 'fa',     name: 'Persian',                native: 'فارسی',             rtl: true  },
  { code: 'ur',     name: 'Urdu',                   native: 'اردو',              rtl: true  },
  { code: 'hi',     name: 'Hindi',                  native: 'हिन्दी',              rtl: false },
  { code: 'ne',     name: 'Nepali',                 native: 'नेपाली',             rtl: false },
  { code: 'bn',     name: 'Bengali',                native: 'বাংলা',              rtl: false },
  { code: 'ta',     name: 'Tamil',                  native: 'தமிழ்',             rtl: false },
  { code: 'te',     name: 'Telugu',                 native: 'తెలుగు',             rtl: false },
  { code: 'ml',     name: 'Malayalam',              native: 'മലയാളം',           rtl: false },
  { code: 'mr',     name: 'Marathi',                native: 'मराठी',              rtl: false },
  { code: 'gu',     name: 'Gujarati',               native: 'ગુજરાતી',             rtl: false },
  { code: 'pa',     name: 'Punjabi',                native: 'ਪੰਜਾਬੀ',              rtl: false },
  { code: 'th',     name: 'Thai',                   native: 'ไทย',               rtl: false },
  { code: 'vi',     name: 'Vietnamese',             native: 'Tiếng Việt',        rtl: false },
  { code: 'id',     name: 'Indonesian',             native: 'Bahasa Indonesia',  rtl: false },
  { code: 'ms',     name: 'Malay',                  native: 'Bahasa Melayu',     rtl: false },
  { code: 'fil',    name: 'Filipino',               native: 'Filipino',          rtl: false },
  { code: 'ja',     name: 'Japanese',               native: '日本語',             rtl: false },
  { code: 'ko',     name: 'Korean',                 native: '한국어',             rtl: false },
  { code: 'zh',     name: 'Chinese (Simplified)',   native: '简体中文',           rtl: false },
  { code: 'zh-TW',  name: 'Chinese (Traditional)',  native: '繁體中文',           rtl: false },
  { code: 'yue',    name: 'Cantonese',              native: '粵語',              rtl: false },
  { code: 'sw',     name: 'Swahili',                native: 'Kiswahili',         rtl: false },
  { code: 'af',     name: 'Afrikaans',              native: 'Afrikaans',         rtl: false },
  { code: 'zu',     name: 'Zulu',                   native: 'isiZulu',           rtl: false },
  { code: 'am',     name: 'Amharic',                native: 'አማርኛ',              rtl: false },
  { code: 'ca',     name: 'Catalan',                native: 'Català',            rtl: false },
  { code: 'eu',     name: 'Basque',                 native: 'Euskara',           rtl: false },
  { code: 'gl',     name: 'Galician',               native: 'Galego',            rtl: false },
  { code: 'mt',     name: 'Maltese',                native: 'Malti',             rtl: false },
  { code: 'cy',     name: 'Welsh',                  native: 'Cymraeg',           rtl: false },
  { code: 'ga',     name: 'Irish',                  native: 'Gaeilge',           rtl: false },
];

// Default (English) strings — every other locale falls back to these.
const EN = {
  submit: 'Submit',
  submitting: 'Submitting...',
  next: 'Next',
  previous: 'Previous',
  back: 'Back',
  required: 'This field is required',
  invalid_email: 'Please enter a valid email address',
  invalid_url: 'Please enter a valid URL',
  invalid_phone: 'Please enter a valid phone number',
  loading: 'Loading...',
  sign_above: 'Sign above',
  clear: 'Clear',
  drop_or_click_upload: 'Drop files here or click to upload',
  allowed: 'Allowed',
  max_file_size: 'Max file size',
  upload_failed: 'Upload failed',
  file_too_large: 'File too large. Maximum size is {size}MB',
  file_type_not_allowed: 'File type not allowed',
  press_enter: 'Press Enter ↵',
  thank_you: 'Thank you for your submission!',
  made_with: 'made with Fantastic Forms',
};

// Per-language overrides (keys that aren't translated will fall back to English).
const T = {
  en: EN,
  es: {
    submit: 'Enviar', submitting: 'Enviando...', next: 'Siguiente', previous: 'Anterior', back: 'Atrás',
    required: 'Este campo es obligatorio', invalid_email: 'Introduce un correo válido',
    invalid_url: 'Introduce una URL válida', invalid_phone: 'Introduce un número de teléfono válido',
    loading: 'Cargando...', sign_above: 'Firma arriba', clear: 'Borrar',
    drop_or_click_upload: 'Suelta archivos aquí o haz clic para subir', allowed: 'Permitido',
    max_file_size: 'Tamaño máximo', upload_failed: 'Error al subir',
    file_too_large: 'Archivo demasiado grande. Máximo {size}MB',
    file_type_not_allowed: 'Tipo de archivo no permitido', press_enter: 'Pulsa Enter ↵',
    thank_you: '¡Gracias por tu envío!', made_with: 'hecho con Fantastic Forms',
  },
  fr: {
    submit: 'Envoyer', submitting: 'Envoi...', next: 'Suivant', previous: 'Précédent', back: 'Retour',
    required: 'Ce champ est requis', invalid_email: 'Entrez un email valide',
    invalid_url: 'Entrez une URL valide', invalid_phone: 'Entrez un numéro valide',
    loading: 'Chargement...', sign_above: 'Signez ci-dessus', clear: 'Effacer',
    drop_or_click_upload: 'Déposez ou cliquez pour téléverser', allowed: 'Autorisé',
    max_file_size: 'Taille max.', upload_failed: 'Échec du téléversement',
    file_too_large: 'Fichier trop volumineux. Maximum {size}MB',
    file_type_not_allowed: 'Type de fichier non autorisé', press_enter: 'Appuyez sur Entrée ↵',
    thank_you: 'Merci pour votre envoi !', made_with: 'fait avec Fantastic Forms',
  },
  de: {
    submit: 'Absenden', submitting: 'Wird gesendet...', next: 'Weiter', previous: 'Zurück', back: 'Zurück',
    required: 'Dieses Feld ist erforderlich', invalid_email: 'Gültige E-Mail eingeben',
    invalid_url: 'Gültige URL eingeben', invalid_phone: 'Gültige Telefonnummer eingeben',
    loading: 'Lädt...', sign_above: 'Hier unterschreiben', clear: 'Löschen',
    drop_or_click_upload: 'Dateien hierher ziehen oder klicken', allowed: 'Erlaubt',
    max_file_size: 'Max. Dateigröße', upload_failed: 'Upload fehlgeschlagen',
    file_too_large: 'Datei zu groß. Maximal {size}MB',
    file_type_not_allowed: 'Dateityp nicht erlaubt', press_enter: 'Enter drücken ↵',
    thank_you: 'Danke für Ihre Einreichung!', made_with: 'erstellt mit Fantastic Forms',
  },
  it: {
    submit: 'Invia', submitting: 'Invio...', next: 'Avanti', previous: 'Indietro', back: 'Indietro',
    required: 'Questo campo è obbligatorio', invalid_email: 'Inserisci un\'email valida',
    invalid_url: 'Inserisci un URL valido', invalid_phone: 'Inserisci un numero valido',
    loading: 'Caricamento...', sign_above: 'Firma sopra', clear: 'Cancella',
    drop_or_click_upload: 'Trascina o clicca per caricare', allowed: 'Consentito',
    max_file_size: 'Dim. massima', upload_failed: 'Caricamento fallito',
    file_too_large: 'File troppo grande. Massimo {size}MB',
    file_type_not_allowed: 'Tipo di file non consentito', press_enter: 'Premi Invio ↵',
    thank_you: 'Grazie per il tuo invio!', made_with: 'realizzato con Fantastic Forms',
  },
  pt: {
    submit: 'Enviar', submitting: 'A enviar...', next: 'Seguinte', previous: 'Anterior', back: 'Voltar',
    required: 'Este campo é obrigatório', invalid_email: 'Insira um email válido',
    invalid_url: 'Insira um URL válido', invalid_phone: 'Insira um telefone válido',
    loading: 'A carregar...', sign_above: 'Assine acima', clear: 'Limpar',
    drop_or_click_upload: 'Largue ficheiros ou clique para enviar', allowed: 'Permitido',
    max_file_size: 'Tamanho máx.', upload_failed: 'Falha no envio',
    file_too_large: 'Ficheiro demasiado grande. Máx. {size}MB',
    file_type_not_allowed: 'Tipo de ficheiro não permitido', press_enter: 'Pressione Enter ↵',
    thank_you: 'Obrigado pelo envio!', made_with: 'feito com Fantastic Forms',
  },
  nl: {
    submit: 'Verzenden', submitting: 'Verzenden...', next: 'Volgende', previous: 'Vorige', back: 'Terug',
    required: 'Dit veld is verplicht', invalid_email: 'Voer een geldig e-mailadres in',
    invalid_url: 'Voer een geldige URL in', invalid_phone: 'Voer een geldig telefoonnummer in',
    loading: 'Laden...', sign_above: 'Onderteken hierboven', clear: 'Wissen',
    drop_or_click_upload: 'Sleep bestanden of klik om te uploaden', allowed: 'Toegestaan',
    max_file_size: 'Max. grootte', upload_failed: 'Upload mislukt',
    file_too_large: 'Bestand te groot. Maximaal {size}MB',
    file_type_not_allowed: 'Bestandstype niet toegestaan', press_enter: 'Druk op Enter ↵',
    thank_you: 'Bedankt voor je inzending!', made_with: 'gemaakt met Fantastic Forms',
  },
  sv: {
    submit: 'Skicka', submitting: 'Skickar...', next: 'Nästa', previous: 'Föregående', back: 'Tillbaka',
    required: 'Detta fält är obligatoriskt', invalid_email: 'Ange en giltig e-postadress',
    invalid_url: 'Ange en giltig URL', invalid_phone: 'Ange ett giltigt telefonnummer',
    loading: 'Laddar...', sign_above: 'Signera ovan', clear: 'Rensa',
    drop_or_click_upload: 'Släpp filer eller klicka för att ladda upp', allowed: 'Tillåtna',
    max_file_size: 'Max storlek', upload_failed: 'Uppladdning misslyckades',
    file_too_large: 'Filen är för stor. Max {size}MB',
    file_type_not_allowed: 'Filtyp ej tillåten', press_enter: 'Tryck Enter ↵',
    thank_you: 'Tack för ditt svar!', made_with: 'skapat med Fantastic Forms',
  },
  no: {
    submit: 'Send', submitting: 'Sender...', next: 'Neste', previous: 'Forrige', back: 'Tilbake',
    required: 'Dette feltet er påkrevd', invalid_email: 'Skriv inn en gyldig e-postadresse',
    invalid_url: 'Skriv inn en gyldig URL', invalid_phone: 'Skriv inn et gyldig telefonnummer',
    loading: 'Laster...', sign_above: 'Signer over', clear: 'Tøm',
    drop_or_click_upload: 'Slipp filer eller klikk for å laste opp', allowed: 'Tillatt',
    max_file_size: 'Maks størrelse', upload_failed: 'Opplasting feilet',
    file_too_large: 'Filen er for stor. Maks {size}MB',
    file_type_not_allowed: 'Filtype ikke tillatt', press_enter: 'Trykk Enter ↵',
    thank_you: 'Takk for innsendingen!', made_with: 'laget med Fantastic Forms',
  },
  da: {
    submit: 'Send', submitting: 'Sender...', next: 'Næste', previous: 'Forrige', back: 'Tilbage',
    required: 'Dette felt er påkrævet', invalid_email: 'Indtast en gyldig e-mailadresse',
    invalid_url: 'Indtast en gyldig URL', invalid_phone: 'Indtast et gyldigt telefonnummer',
    loading: 'Indlæser...', sign_above: 'Underskriv ovenfor', clear: 'Ryd',
    drop_or_click_upload: 'Slip filer eller klik for at uploade', allowed: 'Tilladt',
    max_file_size: 'Maks. størrelse', upload_failed: 'Upload mislykkedes',
    file_too_large: 'Filen er for stor. Maks {size}MB',
    file_type_not_allowed: 'Filtype ikke tilladt', press_enter: 'Tryk Enter ↵',
    thank_you: 'Tak for din indsendelse!', made_with: 'lavet med Fantastic Forms',
  },
  fi: {
    submit: 'Lähetä', submitting: 'Lähetetään...', next: 'Seuraava', previous: 'Edellinen', back: 'Takaisin',
    required: 'Tämä kenttä on pakollinen', invalid_email: 'Anna kelvollinen sähköpostiosoite',
    invalid_url: 'Anna kelvollinen URL', invalid_phone: 'Anna kelvollinen puhelinnumero',
    loading: 'Ladataan...', sign_above: 'Allekirjoita yllä', clear: 'Tyhjennä',
    drop_or_click_upload: 'Pudota tiedostot tai napsauta ladataksesi', allowed: 'Sallittu',
    max_file_size: 'Maksimikoko', upload_failed: 'Lataus epäonnistui',
    file_too_large: 'Tiedosto liian suuri. Enintään {size}MB',
    file_type_not_allowed: 'Tiedostotyyppi ei sallittu', press_enter: 'Paina Enter ↵',
    thank_you: 'Kiitos vastauksestasi!', made_with: 'tehty Fantastic Formsilla',
  },
  is: {
    submit: 'Senda', submitting: 'Sendi...', next: 'Næsta', previous: 'Fyrri', back: 'Til baka',
    required: 'Þennan reit verður að fylla út', invalid_email: 'Sláðu inn gilt netfang',
    invalid_url: 'Sláðu inn gilda slóð', invalid_phone: 'Sláðu inn gilt símanúmer',
    loading: 'Hleður...', sign_above: 'Undirskrifaðu hér að ofan', clear: 'Hreinsa',
    drop_or_click_upload: 'Slepptu skrám eða smelltu til að hlaða upp', allowed: 'Leyft',
    max_file_size: 'Hámarksstærð', upload_failed: 'Upphleðsla mistókst',
    file_too_large: 'Skrá er of stór. Hámark {size}MB',
    file_type_not_allowed: 'Skráartegund ekki leyfð', press_enter: 'Ýttu á Enter ↵',
    thank_you: 'Takk fyrir!', made_with: 'búið til með Fantastic Forms',
  },
  pl: {
    submit: 'Wyślij', submitting: 'Wysyłanie...', next: 'Dalej', previous: 'Wstecz', back: 'Wstecz',
    required: 'To pole jest wymagane', invalid_email: 'Wprowadź prawidłowy adres e-mail',
    invalid_url: 'Wprowadź prawidłowy URL', invalid_phone: 'Wprowadź prawidłowy numer telefonu',
    loading: 'Ładowanie...', sign_above: 'Podpisz powyżej', clear: 'Wyczyść',
    drop_or_click_upload: 'Upuść pliki lub kliknij, aby przesłać', allowed: 'Dozwolone',
    max_file_size: 'Maks. rozmiar', upload_failed: 'Przesyłanie nieudane',
    file_too_large: 'Plik za duży. Maks. {size}MB',
    file_type_not_allowed: 'Typ pliku niedozwolony', press_enter: 'Naciśnij Enter ↵',
    thank_you: 'Dziękujemy za przesłanie!', made_with: 'stworzone z Fantastic Forms',
  },
  cs: {
    submit: 'Odeslat', submitting: 'Odesílání...', next: 'Další', previous: 'Předchozí', back: 'Zpět',
    required: 'Toto pole je povinné', invalid_email: 'Zadejte platnou e-mailovou adresu',
    invalid_url: 'Zadejte platnou URL', invalid_phone: 'Zadejte platné telefonní číslo',
    loading: 'Načítání...', sign_above: 'Podepište se výše', clear: 'Vymazat',
    drop_or_click_upload: 'Přetáhněte soubory nebo klikněte pro nahrání', allowed: 'Povoleno',
    max_file_size: 'Max. velikost', upload_failed: 'Nahrávání selhalo',
    file_too_large: 'Soubor je příliš velký. Max. {size}MB',
    file_type_not_allowed: 'Typ souboru není povolen', press_enter: 'Stiskněte Enter ↵',
    thank_you: 'Děkujeme za odeslání!', made_with: 'vytvořeno s Fantastic Forms',
  },
  sk: {
    submit: 'Odoslať', submitting: 'Odosielanie...', next: 'Ďalej', previous: 'Späť', back: 'Späť',
    required: 'Toto pole je povinné', invalid_email: 'Zadajte platnú e-mailovú adresu',
    invalid_url: 'Zadajte platnú URL', invalid_phone: 'Zadajte platné telefónne číslo',
    loading: 'Načítava sa...', sign_above: 'Podpíšte vyššie', clear: 'Vymazať',
    drop_or_click_upload: 'Pretiahnite súbory alebo kliknite pre nahranie', allowed: 'Povolené',
    max_file_size: 'Max. veľkosť', upload_failed: 'Nahrávanie zlyhalo',
    file_too_large: 'Súbor je príliš veľký. Max. {size}MB',
    file_type_not_allowed: 'Typ súboru nie je povolený', press_enter: 'Stlačte Enter ↵',
    thank_you: 'Ďakujeme za odoslanie!', made_with: 'vytvorené s Fantastic Forms',
  },
  hu: {
    submit: 'Küldés', submitting: 'Küldés...', next: 'Tovább', previous: 'Vissza', back: 'Vissza',
    required: 'Ez a mező kötelező', invalid_email: 'Adjon meg érvényes e-mail címet',
    invalid_url: 'Adjon meg érvényes URL-t', invalid_phone: 'Adjon meg érvényes telefonszámot',
    loading: 'Betöltés...', sign_above: 'Írja alá fent', clear: 'Törlés',
    drop_or_click_upload: 'Húzza ide a fájlokat vagy kattintson a feltöltéshez', allowed: 'Engedélyezett',
    max_file_size: 'Max. méret', upload_failed: 'Feltöltés sikertelen',
    file_too_large: 'Fájl túl nagy. Max. {size}MB',
    file_type_not_allowed: 'Fájltípus nem engedélyezett', press_enter: 'Nyomja meg az Entert ↵',
    thank_you: 'Köszönjük a beküldést!', made_with: 'Fantastic Forms-szal készült',
  },
  ro: {
    submit: 'Trimite', submitting: 'Se trimite...', next: 'Înainte', previous: 'Înapoi', back: 'Înapoi',
    required: 'Acest câmp este obligatoriu', invalid_email: 'Introduceți o adresă de email validă',
    invalid_url: 'Introduceți un URL valid', invalid_phone: 'Introduceți un număr de telefon valid',
    loading: 'Se încarcă...', sign_above: 'Semnați mai sus', clear: 'Șterge',
    drop_or_click_upload: 'Glisați fișierele sau dați clic pentru a încărca', allowed: 'Permis',
    max_file_size: 'Dim. max.', upload_failed: 'Încărcare eșuată',
    file_too_large: 'Fișier prea mare. Max {size}MB',
    file_type_not_allowed: 'Tip de fișier nepermis', press_enter: 'Apăsați Enter ↵',
    thank_you: 'Mulțumim pentru trimitere!', made_with: 'creat cu Fantastic Forms',
  },
  bg: {
    submit: 'Изпрати', submitting: 'Изпращане...', next: 'Напред', previous: 'Назад', back: 'Назад',
    required: 'Това поле е задължително', invalid_email: 'Въведете валиден имейл',
    invalid_url: 'Въведете валиден URL', invalid_phone: 'Въведете валиден телефон',
    loading: 'Зареждане...', sign_above: 'Подпишете отгоре', clear: 'Изчисти',
    drop_or_click_upload: 'Пуснете файлове или щракнете за качване', allowed: 'Разрешени',
    max_file_size: 'Макс. размер', upload_failed: 'Качването неуспешно',
    file_too_large: 'Файлът е твърде голям. Макс. {size}MB',
    file_type_not_allowed: 'Типът файл не е разрешен', press_enter: 'Натиснете Enter ↵',
    thank_you: 'Благодарим за изпращането!', made_with: 'създадено с Fantastic Forms',
  },
  el: {
    submit: 'Υποβολή', submitting: 'Υποβολή...', next: 'Επόμενο', previous: 'Προηγούμενο', back: 'Πίσω',
    required: 'Αυτό το πεδίο είναι υποχρεωτικό', invalid_email: 'Εισάγετε έγκυρο email',
    invalid_url: 'Εισάγετε έγκυρο URL', invalid_phone: 'Εισάγετε έγκυρο τηλέφωνο',
    loading: 'Φόρτωση...', sign_above: 'Υπογράψτε παραπάνω', clear: 'Καθαρισμός',
    drop_or_click_upload: 'Σύρετε αρχεία ή κάντε κλικ', allowed: 'Επιτρέπονται',
    max_file_size: 'Μέγ. μέγεθος', upload_failed: 'Αποτυχία μεταφόρτωσης',
    file_too_large: 'Πολύ μεγάλο αρχείο. Μέγ. {size}MB',
    file_type_not_allowed: 'Μη επιτρεπτός τύπος αρχείου', press_enter: 'Πατήστε Enter ↵',
    thank_you: 'Ευχαριστούμε για την υποβολή!', made_with: 'φτιαγμένο με Fantastic Forms',
  },
  hr: {
    submit: 'Pošalji', submitting: 'Slanje...', next: 'Dalje', previous: 'Natrag', back: 'Natrag',
    required: 'Ovo polje je obavezno', invalid_email: 'Unesite valjanu e-mail adresu',
    invalid_url: 'Unesite valjani URL', invalid_phone: 'Unesite valjani telefon',
    loading: 'Učitavanje...', sign_above: 'Potpišite iznad', clear: 'Obriši',
    drop_or_click_upload: 'Ispustite datoteke ili kliknite za prijenos', allowed: 'Dozvoljeno',
    max_file_size: 'Maks. veličina', upload_failed: 'Prijenos neuspješan',
    file_too_large: 'Datoteka prevelika. Maks. {size}MB',
    file_type_not_allowed: 'Vrsta datoteke nije dozvoljena', press_enter: 'Pritisnite Enter ↵',
    thank_you: 'Hvala na slanju!', made_with: 'izrađeno s Fantastic Forms',
  },
  sr: {
    submit: 'Пошаљи', submitting: 'Слање...', next: 'Даље', previous: 'Назад', back: 'Назад',
    required: 'Ово поље је обавезно', invalid_email: 'Унесите важећу е-маил адресу',
    invalid_url: 'Унесите важећи URL', invalid_phone: 'Унесите важећи телефон',
    loading: 'Учитавање...', sign_above: 'Потпишите изнад', clear: 'Обриши',
    drop_or_click_upload: 'Пустите датотеке или кликните', allowed: 'Дозвољено',
    max_file_size: 'Макс. величина', upload_failed: 'Пренос неуспешан',
    file_too_large: 'Датотека превелика. Макс. {size}MB',
    file_type_not_allowed: 'Тип датотеке није дозвољен', press_enter: 'Притисните Enter ↵',
    thank_you: 'Хвала на слању!', made_with: 'направљено са Fantastic Forms',
  },
  sl: {
    submit: 'Pošlji', submitting: 'Pošiljanje...', next: 'Naprej', previous: 'Nazaj', back: 'Nazaj',
    required: 'To polje je obvezno', invalid_email: 'Vnesite veljaven e-poštni naslov',
    invalid_url: 'Vnesite veljaven URL', invalid_phone: 'Vnesite veljavno telefonsko številko',
    loading: 'Nalaganje...', sign_above: 'Podpišite zgoraj', clear: 'Počisti',
    drop_or_click_upload: 'Spustite datoteke ali kliknite za nalaganje', allowed: 'Dovoljeno',
    max_file_size: 'Najv. velikost', upload_failed: 'Nalaganje ni uspelo',
    file_too_large: 'Datoteka prevelika. Največ {size}MB',
    file_type_not_allowed: 'Vrsta datoteke ni dovoljena', press_enter: 'Pritisnite Enter ↵',
    thank_you: 'Hvala za oddajo!', made_with: 'ustvarjeno s Fantastic Forms',
  },
  lt: {
    submit: 'Siųsti', submitting: 'Siunčiama...', next: 'Toliau', previous: 'Atgal', back: 'Atgal',
    required: 'Šis laukas privalomas', invalid_email: 'Įveskite teisingą el. paštą',
    invalid_url: 'Įveskite teisingą URL', invalid_phone: 'Įveskite teisingą telefono numerį',
    loading: 'Kraunama...', sign_above: 'Pasirašykite virš', clear: 'Išvalyti',
    drop_or_click_upload: 'Numeskite failus arba spustelėkite', allowed: 'Leidžiama',
    max_file_size: 'Maks. dydis', upload_failed: 'Įkėlimas nepavyko',
    file_too_large: 'Failas per didelis. Maks. {size}MB',
    file_type_not_allowed: 'Failo tipas neleidžiamas', press_enter: 'Spauskite Enter ↵',
    thank_you: 'Ačiū už pateikimą!', made_with: 'sukurta su Fantastic Forms',
  },
  lv: {
    submit: 'Iesniegt', submitting: 'Iesniedz...', next: 'Tālāk', previous: 'Atpakaļ', back: 'Atpakaļ',
    required: 'Šis lauks ir obligāts', invalid_email: 'Ievadiet derīgu e-pastu',
    invalid_url: 'Ievadiet derīgu URL', invalid_phone: 'Ievadiet derīgu tālruņa numuru',
    loading: 'Ielādē...', sign_above: 'Parakstieties augstāk', clear: 'Notīrīt',
    drop_or_click_upload: 'Nometiet failus vai noklikšķiniet', allowed: 'Atļauts',
    max_file_size: 'Maks. izmērs', upload_failed: 'Augšupielāde neizdevās',
    file_too_large: 'Fails pārāk liels. Maks. {size}MB',
    file_type_not_allowed: 'Faila tips nav atļauts', press_enter: 'Nospiediet Enter ↵',
    thank_you: 'Paldies par iesniegumu!', made_with: 'izveidots ar Fantastic Forms',
  },
  et: {
    submit: 'Saada', submitting: 'Saatmine...', next: 'Edasi', previous: 'Tagasi', back: 'Tagasi',
    required: 'See väli on kohustuslik', invalid_email: 'Sisestage kehtiv e-posti aadress',
    invalid_url: 'Sisestage kehtiv URL', invalid_phone: 'Sisestage kehtiv telefoninumber',
    loading: 'Laadimine...', sign_above: 'Allkirjastage ülal', clear: 'Tühjenda',
    drop_or_click_upload: 'Lohistage failid või klõpsake', allowed: 'Lubatud',
    max_file_size: 'Max suurus', upload_failed: 'Üleslaadimine ebaõnnestus',
    file_too_large: 'Fail liiga suur. Max {size}MB',
    file_type_not_allowed: 'Failitüüp pole lubatud', press_enter: 'Vajutage Enter ↵',
    thank_you: 'Aitäh esitamise eest!', made_with: 'tehtud Fantastic Formsiga',
  },
  ru: {
    submit: 'Отправить', submitting: 'Отправка...', next: 'Далее', previous: 'Назад', back: 'Назад',
    required: 'Это поле обязательно', invalid_email: 'Введите действительный email',
    invalid_url: 'Введите действительный URL', invalid_phone: 'Введите действительный телефон',
    loading: 'Загрузка...', sign_above: 'Подпишите выше', clear: 'Очистить',
    drop_or_click_upload: 'Перетащите файлы или нажмите для загрузки', allowed: 'Разрешено',
    max_file_size: 'Макс. размер', upload_failed: 'Загрузка не удалась',
    file_too_large: 'Файл слишком большой. Макс. {size}MB',
    file_type_not_allowed: 'Тип файла не разрешён', press_enter: 'Нажмите Enter ↵',
    thank_you: 'Спасибо за отправку!', made_with: 'сделано в Fantastic Forms',
  },
  uk: {
    submit: 'Надіслати', submitting: 'Надсилання...', next: 'Далі', previous: 'Назад', back: 'Назад',
    required: 'Це поле обов\'язкове', invalid_email: 'Введіть дійсний email',
    invalid_url: 'Введіть дійсний URL', invalid_phone: 'Введіть дійсний телефон',
    loading: 'Завантаження...', sign_above: 'Підпишіть вище', clear: 'Очистити',
    drop_or_click_upload: 'Перетягніть файли або клацніть', allowed: 'Дозволено',
    max_file_size: 'Макс. розмір', upload_failed: 'Завантаження не вдалося',
    file_too_large: 'Файл завеликий. Макс. {size}MB',
    file_type_not_allowed: 'Тип файлу не дозволено', press_enter: 'Натисніть Enter ↵',
    thank_you: 'Дякуємо за відправку!', made_with: 'створено з Fantastic Forms',
  },
  be: {
    submit: 'Адправіць', submitting: 'Адпраўка...', next: 'Далей', previous: 'Назад', back: 'Назад',
    required: 'Гэта поле абавязковае', invalid_email: 'Увядзіце сапраўдны email',
    invalid_url: 'Увядзіце сапраўдны URL', invalid_phone: 'Увядзіце сапраўдны тэлефон',
    loading: 'Загрузка...', sign_above: 'Падпішыце вышэй', clear: 'Ачысціць',
    drop_or_click_upload: 'Перацягніце файлы або націсніце', allowed: 'Дазволена',
    max_file_size: 'Макс. памер', upload_failed: 'Загрузка не атрымалася',
    file_too_large: 'Файл завялікі. Макс. {size}MB',
    file_type_not_allowed: 'Тып файла не дазволены', press_enter: 'Націсніце Enter ↵',
    thank_you: 'Дзякуй за адпраўку!', made_with: 'зроблена ў Fantastic Forms',
  },
  tr: {
    submit: 'Gönder', submitting: 'Gönderiliyor...', next: 'İleri', previous: 'Geri', back: 'Geri',
    required: 'Bu alan zorunludur', invalid_email: 'Geçerli bir e-posta girin',
    invalid_url: 'Geçerli bir URL girin', invalid_phone: 'Geçerli bir telefon girin',
    loading: 'Yükleniyor...', sign_above: 'Yukarıdan imzalayın', clear: 'Temizle',
    drop_or_click_upload: 'Dosyaları bırakın veya yüklemek için tıklayın', allowed: 'İzin verilen',
    max_file_size: 'Maks. boyut', upload_failed: 'Yükleme başarısız',
    file_too_large: 'Dosya çok büyük. Maks. {size}MB',
    file_type_not_allowed: 'Dosya türüne izin verilmiyor', press_enter: 'Enter\'a basın ↵',
    thank_you: 'Gönderiminiz için teşekkürler!', made_with: 'Fantastic Forms ile yapıldı',
  },
  az: {
    submit: 'Göndər', submitting: 'Göndərilir...', next: 'İrəli', previous: 'Geri', back: 'Geri',
    required: 'Bu sahə tələb olunur', invalid_email: 'Düzgün email daxil edin',
    invalid_url: 'Düzgün URL daxil edin', invalid_phone: 'Düzgün telefon daxil edin',
    loading: 'Yüklənir...', sign_above: 'Yuxarıda imzalayın', clear: 'Təmizlə',
    drop_or_click_upload: 'Faylları buraxın və ya klikləyin', allowed: 'İcazə verilir',
    max_file_size: 'Maks. ölçü', upload_failed: 'Yükləmə uğursuz',
    file_too_large: 'Fayl çox böyükdür. Maks. {size}MB',
    file_type_not_allowed: 'Fayl növünə icazə verilmir', press_enter: 'Enter basın ↵',
    thank_you: 'Göndərdiyiniz üçün təşəkkürlər!', made_with: 'Fantastic Forms ilə hazırlanıb',
  },
  kk: {
    submit: 'Жіберу', submitting: 'Жіберілуде...', next: 'Келесі', previous: 'Артқа', back: 'Артқа',
    required: 'Бұл өріс міндетті', invalid_email: 'Дұрыс email енгізіңіз',
    invalid_url: 'Дұрыс URL енгізіңіз', invalid_phone: 'Дұрыс телефон енгізіңіз',
    loading: 'Жүктелуде...', sign_above: 'Жоғарыда қол қойыңыз', clear: 'Тазалау',
    drop_or_click_upload: 'Файлдарды тастаңыз немесе шертіңіз', allowed: 'Рұқсат етілген',
    max_file_size: 'Макс. өлшем', upload_failed: 'Жүктеу сәтсіз',
    file_too_large: 'Файл өте үлкен. Макс. {size}MB',
    file_type_not_allowed: 'Файл түрі рұқсат етілмеген', press_enter: 'Enter басыңыз ↵',
    thank_you: 'Жібергеніңізге рахмет!', made_with: 'Fantastic Forms-та жасалған',
  },
  ar: {
    submit: 'إرسال', submitting: 'جارٍ الإرسال...', next: 'التالي', previous: 'السابق', back: 'رجوع',
    required: 'هذا الحقل مطلوب', invalid_email: 'يرجى إدخال بريد إلكتروني صالح',
    invalid_url: 'يرجى إدخال رابط صالح', invalid_phone: 'يرجى إدخال رقم هاتف صالح',
    loading: 'جارٍ التحميل...', sign_above: 'وقّع أعلاه', clear: 'مسح',
    drop_or_click_upload: 'أسقط الملفات هنا أو انقر للتحميل', allowed: 'المسموح',
    max_file_size: 'الحد الأقصى للحجم', upload_failed: 'فشل التحميل',
    file_too_large: 'الملف كبير جدًا. الحد الأقصى {size}MB',
    file_type_not_allowed: 'نوع الملف غير مسموح', press_enter: 'اضغط Enter ↵',
    thank_you: 'شكراً لتقديمك!', made_with: 'صُنع بـ Fantastic Forms',
  },
  he: {
    submit: 'שלח', submitting: 'שולח...', next: 'הבא', previous: 'הקודם', back: 'חזרה',
    required: 'שדה זה הוא חובה', invalid_email: 'הזן כתובת אימייל חוקית',
    invalid_url: 'הזן URL חוקי', invalid_phone: 'הזן מספר טלפון חוקי',
    loading: 'טוען...', sign_above: 'חתום למעלה', clear: 'נקה',
    drop_or_click_upload: 'גרור קבצים או לחץ להעלאה', allowed: 'מותר',
    max_file_size: 'גודל מקסימלי', upload_failed: 'ההעלאה נכשלה',
    file_too_large: 'הקובץ גדול מדי. מקסימום {size}MB',
    file_type_not_allowed: 'סוג קובץ לא מורשה', press_enter: 'לחץ Enter ↵',
    thank_you: 'תודה על השליחה!', made_with: 'נוצר עם Fantastic Forms',
  },
  fa: {
    submit: 'ارسال', submitting: 'در حال ارسال...', next: 'بعدی', previous: 'قبلی', back: 'بازگشت',
    required: 'این فیلد الزامی است', invalid_email: 'ایمیل معتبر وارد کنید',
    invalid_url: 'URL معتبر وارد کنید', invalid_phone: 'شماره تلفن معتبر وارد کنید',
    loading: 'در حال بارگذاری...', sign_above: 'بالا را امضا کنید', clear: 'پاک کردن',
    drop_or_click_upload: 'فایل‌ها را رها کنید یا کلیک کنید', allowed: 'مجاز',
    max_file_size: 'حداکثر اندازه', upload_failed: 'بارگذاری ناموفق',
    file_too_large: 'فایل خیلی بزرگ است. حداکثر {size}MB',
    file_type_not_allowed: 'نوع فایل مجاز نیست', press_enter: 'Enter را فشار دهید ↵',
    thank_you: 'از ارسال شما متشکریم!', made_with: 'ساخته‌شده با Fantastic Forms',
  },
  ur: {
    submit: 'بھیجیں', submitting: 'بھیج رہا ہے...', next: 'اگلا', previous: 'پچھلا', back: 'واپس',
    required: 'یہ خانہ ضروری ہے', invalid_email: 'درست ای میل درج کریں',
    invalid_url: 'درست URL درج کریں', invalid_phone: 'درست فون نمبر درج کریں',
    loading: 'لوڈ ہو رہا ہے...', sign_above: 'اوپر دستخط کریں', clear: 'صاف کریں',
    drop_or_click_upload: 'فائلیں چھوڑیں یا کلک کریں', allowed: 'اجازت ہے',
    max_file_size: 'زیادہ سے زیادہ سائز', upload_failed: 'اپ لوڈ ناکام',
    file_too_large: 'فائل بہت بڑی ہے۔ زیادہ سے زیادہ {size}MB',
    file_type_not_allowed: 'فائل کی قسم کی اجازت نہیں', press_enter: 'Enter دبائیں ↵',
    thank_you: 'بھیجنے کا شکریہ!', made_with: 'Fantastic Forms سے بنایا گیا',
  },
  hi: {
    submit: 'भेजें', submitting: 'भेज रहा है...', next: 'अगला', previous: 'पिछला', back: 'वापस',
    required: 'यह फ़ील्ड आवश्यक है', invalid_email: 'मान्य ईमेल दर्ज करें',
    invalid_url: 'मान्य URL दर्ज करें', invalid_phone: 'मान्य फ़ोन दर्ज करें',
    loading: 'लोड हो रहा है...', sign_above: 'ऊपर हस्ताक्षर करें', clear: 'साफ़ करें',
    drop_or_click_upload: 'फ़ाइलें छोड़ें या क्लिक करें', allowed: 'अनुमत',
    max_file_size: 'अधिकतम आकार', upload_failed: 'अपलोड विफल',
    file_too_large: 'फ़ाइल बहुत बड़ी है। अधिकतम {size}MB',
    file_type_not_allowed: 'फ़ाइल प्रकार की अनुमति नहीं', press_enter: 'Enter दबाएँ ↵',
    thank_you: 'आपके सबमिशन के लिए धन्यवाद!', made_with: 'Fantastic Forms से बनाया गया',
  },
  bn: {
    submit: 'পাঠান', submitting: 'পাঠানো হচ্ছে...', next: 'পরবর্তী', previous: 'আগের', back: 'ফিরুন',
    required: 'এই ফিল্ডটি আবশ্যক', invalid_email: 'একটি বৈধ ইমেইল দিন',
    invalid_url: 'একটি বৈধ URL দিন', invalid_phone: 'একটি বৈধ ফোন নম্বর দিন',
    loading: 'লোড হচ্ছে...', sign_above: 'উপরে স্বাক্ষর করুন', clear: 'মুছুন',
    drop_or_click_upload: 'ফাইল ছাড়ুন বা ক্লিক করুন', allowed: 'অনুমোদিত',
    max_file_size: 'সর্বোচ্চ আকার', upload_failed: 'আপলোড ব্যর্থ',
    file_too_large: 'ফাইল অনেক বড়। সর্বোচ্চ {size}MB',
    file_type_not_allowed: 'ফাইলের ধরন অনুমোদিত নয়', press_enter: 'Enter চাপুন ↵',
    thank_you: 'আপনার জমার জন্য ধন্যবাদ!', made_with: 'Fantastic Forms দিয়ে তৈরি',
  },
  ta: {
    submit: 'சமர்ப்பி', submitting: 'அனுப்புகிறது...', next: 'அடுத்து', previous: 'முந்தைய', back: 'பின்',
    required: 'இந்த புலம் தேவை', invalid_email: 'சரியான மின்னஞ்சல் கொடுக்கவும்',
    invalid_url: 'சரியான URL கொடுக்கவும்', invalid_phone: 'சரியான தொலைபேசி எண் கொடுக்கவும்',
    loading: 'ஏற்றுகிறது...', sign_above: 'மேலே கையெழுத்திடவும்', clear: 'அழி',
    drop_or_click_upload: 'கோப்புகளை விடவும் அல்லது கிளிக் செய்யவும்', allowed: 'அனுமதிக்கப்பட்டது',
    max_file_size: 'அதிகபட்ச அளவு', upload_failed: 'பதிவேற்றம் தோல்வி',
    file_too_large: 'கோப்பு மிக பெரியது. அதிகபட்சம் {size}MB',
    file_type_not_allowed: 'கோப்பு வகை அனுமதிக்கப்படவில்லை', press_enter: 'Enter அழுத்தவும் ↵',
    thank_you: 'சமர்ப்பணைக்கு நன்றி!', made_with: 'Fantastic Forms மூலம் உருவாக்கப்பட்டது',
  },
  te: {
    submit: 'సమర్పించండి', submitting: 'పంపుతోంది...', next: 'తదుపరి', previous: 'మునుపటి', back: 'వెనుకకు',
    required: 'ఈ ఫీల్డ్ తప్పనిసరి', invalid_email: 'సరైన ఇమెయిల్ నమోదు చేయండి',
    invalid_url: 'సరైన URL నమోదు చేయండి', invalid_phone: 'సరైన ఫోన్ నంబర్ నమోదు చేయండి',
    loading: 'లోడ్ అవుతోంది...', sign_above: 'పైన సంతకం చేయండి', clear: 'క్లియర్',
    drop_or_click_upload: 'ఫైళ్ళు వదలండి లేదా క్లిక్ చేయండి', allowed: 'అనుమతించబడింది',
    max_file_size: 'గరిష్ట పరిమాణం', upload_failed: 'అప్‌లోడ్ విఫలమైంది',
    file_too_large: 'ఫైల్ చాలా పెద్దది. గరిష్టం {size}MB',
    file_type_not_allowed: 'ఫైల్ రకం అనుమతించబడలేదు', press_enter: 'Enter నొక్కండి ↵',
    thank_you: 'మీ సమర్పణకు ధన్యవాదాలు!', made_with: 'Fantastic Forms తో తయారు చేయబడింది',
  },
  mr: {
    submit: 'पाठवा', submitting: 'पाठवत आहे...', next: 'पुढे', previous: 'मागे', back: 'मागे',
    required: 'हे फील्ड आवश्यक आहे', invalid_email: 'वैध ईमेल प्रविष्ट करा',
    invalid_url: 'वैध URL प्रविष्ट करा', invalid_phone: 'वैध फोन प्रविष्ट करा',
    loading: 'लोड होत आहे...', sign_above: 'वर स्वाक्षरी करा', clear: 'साफ करा',
    drop_or_click_upload: 'फाइल्स सोडा किंवा क्लिक करा', allowed: 'अनुमत',
    max_file_size: 'कमाल आकार', upload_failed: 'अपलोड अयशस्वी',
    file_too_large: 'फाइल खूप मोठी आहे. कमाल {size}MB',
    file_type_not_allowed: 'फाइल प्रकार अनुमत नाही', press_enter: 'Enter दाबा ↵',
    thank_you: 'सबमिशनबद्दल धन्यवाद!', made_with: 'Fantastic Forms ने तयार केले',
  },
  gu: {
    submit: 'મોકલો', submitting: 'મોકલી રહ્યું છે...', next: 'આગળ', previous: 'પાછળ', back: 'પાછા',
    required: 'આ ફીલ્ડ આવશ્યક છે', invalid_email: 'માન્ય ઈમેઈલ દાખલ કરો',
    invalid_url: 'માન્ય URL દાખલ કરો', invalid_phone: 'માન્ય ફોન દાખલ કરો',
    loading: 'લોડ થઈ રહ્યું છે...', sign_above: 'ઉપર સહી કરો', clear: 'સાફ કરો',
    drop_or_click_upload: 'ફાઈલો છોડો અથવા ક્લિક કરો', allowed: 'મંજૂર',
    max_file_size: 'મહત્તમ કદ', upload_failed: 'અપલોડ નિષ્ફળ',
    file_too_large: 'ફાઈલ ખૂબ મોટી છે. મહત્તમ {size}MB',
    file_type_not_allowed: 'ફાઈલ પ્રકાર મંજૂર નથી', press_enter: 'Enter દબાવો ↵',
    thank_you: 'સબમિશન માટે આભાર!', made_with: 'Fantastic Forms થી બનાવેલ',
  },
  pa: {
    submit: 'ਭੇਜੋ', submitting: 'ਭੇਜ ਰਿਹਾ ਹੈ...', next: 'ਅਗਲਾ', previous: 'ਪਿਛਲਾ', back: 'ਵਾਪਸ',
    required: 'ਇਹ ਫੀਲਡ ਲੋੜੀਂਦਾ ਹੈ', invalid_email: 'ਜਾਇਜ਼ ਈਮੇਲ ਦਰਜ ਕਰੋ',
    invalid_url: 'ਜਾਇਜ਼ URL ਦਰਜ ਕਰੋ', invalid_phone: 'ਜਾਇਜ਼ ਫੋਨ ਦਰਜ ਕਰੋ',
    loading: 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ...', sign_above: 'ਉੱਪਰ ਦਸਤਖਤ ਕਰੋ', clear: 'ਸਾਫ਼ ਕਰੋ',
    drop_or_click_upload: 'ਫਾਈਲਾਂ ਛੱਡੋ ਜਾਂ ਕਲਿੱਕ ਕਰੋ', allowed: 'ਆਗਿਆ ਹੈ',
    max_file_size: 'ਵੱਧ ਤੋਂ ਵੱਧ ਆਕਾਰ', upload_failed: 'ਅਪਲੋਡ ਅਸਫਲ',
    file_too_large: 'ਫਾਈਲ ਬਹੁਤ ਵੱਡੀ ਹੈ। ਵੱਧ ਤੋਂ ਵੱਧ {size}MB',
    file_type_not_allowed: 'ਫਾਈਲ ਕਿਸਮ ਦੀ ਆਗਿਆ ਨਹੀਂ', press_enter: 'Enter ਦਬਾਓ ↵',
    thank_you: 'ਸਬਮਿਸ਼ਨ ਲਈ ਧੰਨਵਾਦ!', made_with: 'Fantastic Forms ਨਾਲ ਬਣਾਇਆ',
  },
  th: {
    submit: 'ส่ง', submitting: 'กำลังส่ง...', next: 'ถัดไป', previous: 'ก่อนหน้า', back: 'กลับ',
    required: 'ต้องกรอกช่องนี้', invalid_email: 'กรอกอีเมลที่ถูกต้อง',
    invalid_url: 'กรอก URL ที่ถูกต้อง', invalid_phone: 'กรอกเบอร์โทรที่ถูกต้อง',
    loading: 'กำลังโหลด...', sign_above: 'เซ็นด้านบน', clear: 'ล้าง',
    drop_or_click_upload: 'วางไฟล์หรือคลิกเพื่ออัปโหลด', allowed: 'ที่อนุญาต',
    max_file_size: 'ขนาดสูงสุด', upload_failed: 'อัปโหลดล้มเหลว',
    file_too_large: 'ไฟล์ใหญ่เกินไป สูงสุด {size}MB',
    file_type_not_allowed: 'ไม่อนุญาตประเภทไฟล์', press_enter: 'กด Enter ↵',
    thank_you: 'ขอบคุณสำหรับการส่ง!', made_with: 'สร้างด้วย Fantastic Forms',
  },
  vi: {
    submit: 'Gửi', submitting: 'Đang gửi...', next: 'Tiếp', previous: 'Quay lại', back: 'Quay lại',
    required: 'Trường này là bắt buộc', invalid_email: 'Nhập email hợp lệ',
    invalid_url: 'Nhập URL hợp lệ', invalid_phone: 'Nhập số điện thoại hợp lệ',
    loading: 'Đang tải...', sign_above: 'Ký phía trên', clear: 'Xóa',
    drop_or_click_upload: 'Thả tệp hoặc nhấp để tải lên', allowed: 'Cho phép',
    max_file_size: 'Kích thước tối đa', upload_failed: 'Tải lên thất bại',
    file_too_large: 'Tệp quá lớn. Tối đa {size}MB',
    file_type_not_allowed: 'Loại tệp không được phép', press_enter: 'Nhấn Enter ↵',
    thank_you: 'Cảm ơn bạn đã gửi!', made_with: 'tạo bằng Fantastic Forms',
  },
  id: {
    submit: 'Kirim', submitting: 'Mengirim...', next: 'Berikutnya', previous: 'Sebelumnya', back: 'Kembali',
    required: 'Bidang ini wajib diisi', invalid_email: 'Masukkan email yang valid',
    invalid_url: 'Masukkan URL yang valid', invalid_phone: 'Masukkan nomor telepon yang valid',
    loading: 'Memuat...', sign_above: 'Tanda tangan di atas', clear: 'Hapus',
    drop_or_click_upload: 'Jatuhkan file atau klik untuk unggah', allowed: 'Diizinkan',
    max_file_size: 'Ukuran maks.', upload_failed: 'Unggah gagal',
    file_too_large: 'File terlalu besar. Maks. {size}MB',
    file_type_not_allowed: 'Tipe file tidak diizinkan', press_enter: 'Tekan Enter ↵',
    thank_you: 'Terima kasih atas kirimannya!', made_with: 'dibuat dengan Fantastic Forms',
  },
  ms: {
    submit: 'Hantar', submitting: 'Menghantar...', next: 'Seterusnya', previous: 'Sebelumnya', back: 'Kembali',
    required: 'Medan ini diperlukan', invalid_email: 'Masukkan e-mel yang sah',
    invalid_url: 'Masukkan URL yang sah', invalid_phone: 'Masukkan nombor telefon yang sah',
    loading: 'Memuatkan...', sign_above: 'Tandatangan di atas', clear: 'Kosongkan',
    drop_or_click_upload: 'Lepaskan fail atau klik untuk muat naik', allowed: 'Dibenarkan',
    max_file_size: 'Saiz maks.', upload_failed: 'Muat naik gagal',
    file_too_large: 'Fail terlalu besar. Maks. {size}MB',
    file_type_not_allowed: 'Jenis fail tidak dibenarkan', press_enter: 'Tekan Enter ↵',
    thank_you: 'Terima kasih atas penyerahan!', made_with: 'dibuat dengan Fantastic Forms',
  },
  fil: {
    submit: 'Ipasa', submitting: 'Nagpapasa...', next: 'Susunod', previous: 'Nakaraan', back: 'Bumalik',
    required: 'Kinakailangan ang patlang na ito', invalid_email: 'Maglagay ng wastong email',
    invalid_url: 'Maglagay ng wastong URL', invalid_phone: 'Maglagay ng wastong numero ng telepono',
    loading: 'Naglo-load...', sign_above: 'Pumirma sa itaas', clear: 'Linisin',
    drop_or_click_upload: 'Ibagsak ang mga file o i-click upang mag-upload', allowed: 'Pinapayagan',
    max_file_size: 'Pinakamalaking laki', upload_failed: 'Nabigong mag-upload',
    file_too_large: 'Masyadong malaki ang file. Maks. {size}MB',
    file_type_not_allowed: 'Hindi pinapayagang uri ng file', press_enter: 'Pindutin ang Enter ↵',
    thank_you: 'Salamat sa pagpapasa!', made_with: 'gawa sa Fantastic Forms',
  },
  ja: {
    submit: '送信', submitting: '送信中...', next: '次へ', previous: '前へ', back: '戻る',
    required: 'この項目は必須です', invalid_email: '有効なメールアドレスを入力してください',
    invalid_url: '有効なURLを入力してください', invalid_phone: '有効な電話番号を入力してください',
    loading: '読み込み中...', sign_above: '上に署名してください', clear: 'クリア',
    drop_or_click_upload: 'ファイルをドロップまたはクリックしてアップロード', allowed: '許可',
    max_file_size: '最大サイズ', upload_failed: 'アップロード失敗',
    file_too_large: 'ファイルが大きすぎます。最大{size}MB',
    file_type_not_allowed: 'ファイル形式が許可されていません', press_enter: 'Enterを押す ↵',
    thank_you: 'ご回答ありがとうございました！', made_with: 'Fantastic Forms で作成',
  },
  ko: {
    submit: '제출', submitting: '제출 중...', next: '다음', previous: '이전', back: '뒤로',
    required: '이 필드는 필수입니다', invalid_email: '유효한 이메일을 입력하세요',
    invalid_url: '유효한 URL을 입력하세요', invalid_phone: '유효한 전화번호를 입력하세요',
    loading: '로딩 중...', sign_above: '위에 서명하세요', clear: '지우기',
    drop_or_click_upload: '파일을 놓거나 클릭하여 업로드', allowed: '허용',
    max_file_size: '최대 크기', upload_failed: '업로드 실패',
    file_too_large: '파일이 너무 큽니다. 최대 {size}MB',
    file_type_not_allowed: '허용되지 않는 파일 유형', press_enter: 'Enter 누르기 ↵',
    thank_you: '제출해 주셔서 감사합니다!', made_with: 'Fantastic Forms로 제작',
  },
  zh: {
    submit: '提交', submitting: '提交中...', next: '下一步', previous: '上一步', back: '返回',
    required: '此字段为必填', invalid_email: '请输入有效的电子邮件',
    invalid_url: '请输入有效的URL', invalid_phone: '请输入有效的电话号码',
    loading: '加载中...', sign_above: '在上方签名', clear: '清除',
    drop_or_click_upload: '拖放文件或点击上传', allowed: '允许',
    max_file_size: '最大尺寸', upload_failed: '上传失败',
    file_too_large: '文件过大。最大{size}MB',
    file_type_not_allowed: '不允许的文件类型', press_enter: '按 Enter ↵',
    thank_you: '感谢您的提交！', made_with: '由 Fantastic Forms 制作',
  },
  'zh-TW': {
    submit: '提交', submitting: '提交中...', next: '下一步', previous: '上一步', back: '返回',
    required: '此欄位為必填', invalid_email: '請輸入有效的電子郵件',
    invalid_url: '請輸入有效的URL', invalid_phone: '請輸入有效的電話號碼',
    loading: '載入中...', sign_above: '在上方簽名', clear: '清除',
    drop_or_click_upload: '拖放檔案或點擊上傳', allowed: '允許',
    max_file_size: '最大大小', upload_failed: '上傳失敗',
    file_too_large: '檔案過大。最大{size}MB',
    file_type_not_allowed: '不允許的檔案類型', press_enter: '按 Enter ↵',
    thank_you: '感謝您的提交！', made_with: '由 Fantastic Forms 製作',
  },
  sw: {
    submit: 'Wasilisha', submitting: 'Inawasilisha...', next: 'Inayofuata', previous: 'Iliyotangulia', back: 'Rudi',
    required: 'Sehemu hii inahitajika', invalid_email: 'Ingiza barua pepe halali',
    invalid_url: 'Ingiza URL halali', invalid_phone: 'Ingiza nambari ya simu halali',
    loading: 'Inapakia...', sign_above: 'Tia saini juu', clear: 'Futa',
    drop_or_click_upload: 'Dondosha faili au bofya kupakia', allowed: 'Inaruhusiwa',
    max_file_size: 'Ukubwa wa juu', upload_failed: 'Upakiaji umeshindwa',
    file_too_large: 'Faili kubwa sana. Kiwango cha juu {size}MB',
    file_type_not_allowed: 'Aina ya faili hairuhusiwi', press_enter: 'Bonyeza Enter ↵',
    thank_you: 'Asante kwa kuwasilisha!', made_with: 'imetengenezwa kwa Fantastic Forms',
  },
  af: {
    submit: 'Stuur', submitting: 'Stuur tans...', next: 'Volgende', previous: 'Vorige', back: 'Terug',
    required: 'Hierdie veld is verpligtend', invalid_email: 'Voer \'n geldige e-pos in',
    invalid_url: 'Voer \'n geldige URL in', invalid_phone: 'Voer \'n geldige telefoonnommer in',
    loading: 'Laai...', sign_above: 'Teken hierbo', clear: 'Maak skoon',
    drop_or_click_upload: 'Los lêers of klik om op te laai', allowed: 'Toegelaat',
    max_file_size: 'Maks. grootte', upload_failed: 'Oplaai het misluk',
    file_too_large: 'Lêer te groot. Maks. {size}MB',
    file_type_not_allowed: 'Lêertipe nie toegelaat nie', press_enter: 'Druk Enter ↵',
    thank_you: 'Dankie vir jou indiening!', made_with: 'gemaak met Fantastic Forms',
  },
  zu: {
    submit: 'Thumela', submitting: 'Iyathumela...', next: 'Okulandelayo', previous: 'Okwedlule', back: 'Buyela',
    required: 'Le nkambu iyadingeka', invalid_email: 'Faka i-imeyili evumelekile',
    invalid_url: 'Faka i-URL evumelekile', invalid_phone: 'Faka inombolo yefoni evumelekile',
    loading: 'Ilayisha...', sign_above: 'Sayina ngenhla', clear: 'Sula',
    drop_or_click_upload: 'Lahla amafayela noma chofoza ukulayisha', allowed: 'Kuvunyelwe',
    max_file_size: 'Usayizi omkhulu', upload_failed: 'Ukulayisha kuhlulekile',
    file_too_large: 'Ifayela likhulu kakhulu. Maks. {size}MB',
    file_type_not_allowed: 'Uhlobo lwefayela aluvunyelwe', press_enter: 'Cindezela u-Enter ↵',
    thank_you: 'Siyabonga ngokuthumela!', made_with: 'kwenziwe nge-Fantastic Forms',
  },
  am: {
    submit: 'አስገባ', submitting: 'በመላክ ላይ...', next: 'ቀጣይ', previous: 'ቀዳሚ', back: 'ተመለስ',
    required: 'ይህ መስክ ያስፈልጋል', invalid_email: 'ትክክለኛ ኢሜይል ያስገቡ',
    invalid_url: 'ትክክለኛ URL ያስገቡ', invalid_phone: 'ትክክለኛ ስልክ ቁጥር ያስገቡ',
    loading: 'በመጫን ላይ...', sign_above: 'ከላይ ይፈርሙ', clear: 'አጽዳ',
    drop_or_click_upload: 'ፋይሎችን ይጣሉ ወይም ጠቅ ያድርጉ', allowed: 'ተፈቅዷል',
    max_file_size: 'ከፍተኛ መጠን', upload_failed: 'መጫን አልተሳካም',
    file_too_large: 'ፋይል በጣም ትልቅ ነው። ከፍተኛ {size}MB',
    file_type_not_allowed: 'የፋይል አይነት አይፈቀድም', press_enter: 'Enter ይጫኑ ↵',
    thank_you: 'ስለላኩልን እናመሰግናለን!', made_with: 'በ Fantastic Forms የተሰራ',
  },
  ca: {
    submit: 'Envia', submitting: 'Enviant...', next: 'Següent', previous: 'Anterior', back: 'Enrere',
    required: 'Aquest camp és obligatori', invalid_email: 'Introdueix un correu vàlid',
    invalid_url: 'Introdueix una URL vàlida', invalid_phone: 'Introdueix un telèfon vàlid',
    loading: 'Carregant...', sign_above: 'Signa a sobre', clear: 'Esborra',
    drop_or_click_upload: 'Deixa anar fitxers o fes clic per pujar', allowed: 'Permès',
    max_file_size: 'Mida màx.', upload_failed: 'Càrrega fallida',
    file_too_large: 'Fitxer massa gran. Màx. {size}MB',
    file_type_not_allowed: 'Tipus de fitxer no permès', press_enter: 'Prem Enter ↵',
    thank_you: 'Gràcies per l\'enviament!', made_with: 'fet amb Fantastic Forms',
  },
  eu: {
    submit: 'Bidali', submitting: 'Bidaltzen...', next: 'Hurrengoa', previous: 'Aurrekoa', back: 'Atzera',
    required: 'Eremu hau beharrezkoa da', invalid_email: 'Sartu baliozko emaila',
    invalid_url: 'Sartu baliozko URL bat', invalid_phone: 'Sartu baliozko telefono zenbakia',
    loading: 'Kargatzen...', sign_above: 'Sinatu goian', clear: 'Garbitu',
    drop_or_click_upload: 'Jaurti fitxategiak edo egin klik kargatzeko', allowed: 'Onartuta',
    max_file_size: 'Geh. tamaina', upload_failed: 'Karga huts egin du',
    file_too_large: 'Fitxategi handiegia. Geh. {size}MB',
    file_type_not_allowed: 'Fitxategi mota ez da onartzen', press_enter: 'Sakatu Enter ↵',
    thank_you: 'Eskerrik asko bidaltzeagatik!', made_with: 'Fantastic Forms-ekin egina',
  },
  gl: {
    submit: 'Enviar', submitting: 'Enviando...', next: 'Seguinte', previous: 'Anterior', back: 'Atrás',
    required: 'Este campo é obrigatorio', invalid_email: 'Insira un correo válido',
    invalid_url: 'Insira un URL válido', invalid_phone: 'Insira un teléfono válido',
    loading: 'Cargando...', sign_above: 'Asine enriba', clear: 'Limpar',
    drop_or_click_upload: 'Solta ficheiros ou fai clic para subir', allowed: 'Permitido',
    max_file_size: 'Tamaño máx.', upload_failed: 'Carga fallida',
    file_too_large: 'Ficheiro demasiado grande. Máx. {size}MB',
    file_type_not_allowed: 'Tipo de ficheiro non permitido', press_enter: 'Preme Intro ↵',
    thank_you: 'Grazas polo envío!', made_with: 'feito con Fantastic Forms',
  },
  mt: {
    submit: 'Ibgħat', submitting: 'Qed jibgħat...', next: 'Li jmiss', previous: 'Preċedenti', back: 'Lura',
    required: 'Dan il-qasam huwa meħtieġ', invalid_email: 'Daħħal email validu',
    invalid_url: 'Daħħal URL validu', invalid_phone: 'Daħħal numru tat-telefon validu',
    loading: 'Qed jitgħabba...', sign_above: 'Iffirma fuq', clear: 'Naddaf',
    drop_or_click_upload: 'Waqqa\' fajls jew ikklikkja biex tuploadja', allowed: 'Permess',
    max_file_size: 'Daqs massimu', upload_failed: 'L-upload falla',
    file_too_large: 'Il-fajl kbir wisq. Massimu {size}MB',
    file_type_not_allowed: 'Tip ta\' fajl mhux permess', press_enter: 'Agħfas Enter ↵',
    thank_you: 'Grazzi tas-sottomissjoni!', made_with: 'magħmul bi Fantastic Forms',
  },
  cy: {
    submit: 'Cyflwyno', submitting: 'Yn cyflwyno...', next: 'Nesaf', previous: 'Blaenorol', back: 'Yn ôl',
    required: 'Mae angen y maes hwn', invalid_email: 'Rhowch e-bost dilys',
    invalid_url: 'Rhowch URL dilys', invalid_phone: 'Rhowch rif ffôn dilys',
    loading: 'Llwytho...', sign_above: 'Llofnodwch uchod', clear: 'Clirio',
    drop_or_click_upload: 'Gollyngwch ffeiliau neu cliciwch i uwchlwytho', allowed: 'Caniateir',
    max_file_size: 'Maint mwyaf', upload_failed: 'Methodd uwchlwytho',
    file_too_large: 'Ffeil yn rhy fawr. Uchafswm {size}MB',
    file_type_not_allowed: 'Math o ffeil heb ei ganiatáu', press_enter: 'Pwyswch Enter ↵',
    thank_you: 'Diolch am gyflwyno!', made_with: 'wedi\'i wneud gyda Fantastic Forms',
  },
  ga: {
    submit: 'Cuir isteach', submitting: 'Á chur isteach...', next: 'Ar aghaidh', previous: 'Siar', back: 'Ar ais',
    required: 'Tá an réimse seo riachtanach', invalid_email: 'Cuir isteach ríomhphost bailí',
    invalid_url: 'Cuir isteach URL bailí', invalid_phone: 'Cuir isteach uimhir ghutháin bhailí',
    loading: 'Á lódáil...', sign_above: 'Sínigh thuas', clear: 'Glan',
    drop_or_click_upload: 'Scaoil comhaid nó cliceáil chun uaslódáil', allowed: 'Ceadaithe',
    max_file_size: 'Uasmhéid', upload_failed: 'Theip ar uaslódáil',
    file_too_large: 'Tá an comhad ró-mhór. Uasmhéid {size}MB',
    file_type_not_allowed: 'Cineál comhaid ní cheadaítear', press_enter: 'Brúigh Enter ↵',
    thank_you: 'Go raibh maith agat as do chur isteach!', made_with: 'déanta le Fantastic Forms',
  },
  sq: {
    submit: 'Dërgo', submitting: 'Duke dërguar...', next: 'Tjetra', previous: 'Mëparshmi', back: 'Mbrapa',
    required: 'Kjo fushë është e detyrueshme', invalid_email: 'Vendosni një email të vlefshëm',
    invalid_url: 'Vendosni një URL të vlefshme', invalid_phone: 'Vendosni një numër telefoni të vlefshëm',
    loading: 'Duke ngarkuar...', sign_above: 'Nënshkruani më lart', clear: 'Pastro',
    drop_or_click_upload: 'Lësho skedarët ose kliko për të ngarkuar', allowed: 'Të lejuara',
    max_file_size: 'Madhësia maks.', upload_failed: 'Ngarkimi dështoi',
    file_too_large: 'Skedari shumë i madh. Maksimumi {size}MB',
    file_type_not_allowed: 'Lloji i skedarit nuk lejohet', press_enter: 'Shtyp Enter ↵',
    thank_you: 'Faleminderit për dërgimin tuaj!', made_with: 'krijuar me Fantastic Forms',
  },
  hy: {
    submit: 'Ուղարկել', submitting: 'Ուղարկվում է...', next: 'Հաջորդը', previous: 'Նախորդը', back: 'Հետ',
    required: 'Այս դաշտը պարտադիր է', invalid_email: 'Մուտքագրեք վավեր էլ. հասցե',
    invalid_url: 'Մուտքագրեք վավեր URL', invalid_phone: 'Մուտքագրեք վավեր հեռախոսահամար',
    loading: 'Բեռնվում է...', sign_above: 'Ստորագրեք վերևում', clear: 'Մաքրել',
    drop_or_click_upload: 'Թողեք ֆայլերը այստեղ կամ սեղմեք վերբեռնելու համար', allowed: 'Թույլատրված է',
    max_file_size: 'Առավելագույն չափը', upload_failed: 'Վերբեռնումը ձախողվեց',
    file_too_large: 'Ֆայլը չափազանց մեծ է. առավելագույնը {size}ՄԲ',
    file_type_not_allowed: 'Ֆայլի տեսակը թույլատրված չէ', press_enter: 'Սեղմեք Enter ↵',
    thank_you: 'Շնորհակալություն ձեր հայտի համար!', made_with: 'ստեղծված է Fantastic Forms-ով',
  },
  ka: {
    submit: 'გაგზავნა', submitting: 'იგზავნება...', next: 'შემდეგი', previous: 'წინა', back: 'უკან',
    required: 'ეს ველი სავალდებულოა', invalid_email: 'შეიყვანეთ სწორი ელ. ფოსტა',
    invalid_url: 'შეიყვანეთ სწორი URL', invalid_phone: 'შეიყვანეთ სწორი ტელეფონის ნომერი',
    loading: 'იტვირთება...', sign_above: 'მოაწერეთ ხელი ზემოთ', clear: 'გასუფთავება',
    drop_or_click_upload: 'ჩამოაგდეთ ფაილები ან დააწკაპუნეთ ასატვირთად', allowed: 'დაშვებულია',
    max_file_size: 'მაქს. ზომა', upload_failed: 'ატვირთვა ვერ მოხერხდა',
    file_too_large: 'ფაილი ძალიან დიდია. მაქს. {size}MB',
    file_type_not_allowed: 'ფაილის ტიპი დაუშვებელია', press_enter: 'დააჭირეთ Enter ↵',
    thank_you: 'მადლობა გაგზავნისთვის!', made_with: 'შექმნილია Fantastic Forms-ით',
  },
  ky: {
    submit: 'Жөнөтүү', submitting: 'Жөнөтүлүүдө...', next: 'Кийинки', previous: 'Мурунку', back: 'Артка',
    required: 'Бул талаа милдеттүү', invalid_email: 'Туура электрондук дарек киргизиңиз',
    invalid_url: 'Туура URL киргизиңиз', invalid_phone: 'Туура телефон номурун киргизиңиз',
    loading: 'Жүктөлүүдө...', sign_above: 'Жогору жагына кол коюңуз', clear: 'Тазалоо',
    drop_or_click_upload: 'Файлдарды ыргытыңыз же чыкылдатыңыз', allowed: 'Уруксат берилген',
    max_file_size: 'Макс. өлчөм', upload_failed: 'Жүктөө ишке ашпады',
    file_too_large: 'Файл өтө чоң. Макс. {size}МБ',
    file_type_not_allowed: 'Файл түрүнө уруксат жок', press_enter: 'Enter басыңыз ↵',
    thank_you: 'Жөнөткөнүңүз үчүн рахмат!', made_with: 'Fantastic Forms менен жасалган',
  },
  uz: {
    submit: 'Yuborish', submitting: 'Yuborilmoqda...', next: 'Keyingi', previous: 'Oldingi', back: 'Orqaga',
    required: 'Bu maydon majburiy', invalid_email: 'Toʻgʻri email kiriting',
    invalid_url: 'Toʻgʻri URL kiriting', invalid_phone: 'Toʻgʻri telefon raqamini kiriting',
    loading: 'Yuklanmoqda...', sign_above: 'Yuqorida imzo qoʻying', clear: 'Tozalash',
    drop_or_click_upload: 'Fayllarni shu yerga tashlang yoki bosing', allowed: 'Ruxsat etilgan',
    max_file_size: 'Maks. hajm', upload_failed: 'Yuklash muvaffaqiyatsiz',
    file_too_large: 'Fayl juda katta. Maks. {size}MB',
    file_type_not_allowed: 'Fayl turi ruxsat etilmagan', press_enter: 'Enter bosing ↵',
    thank_you: 'Yuborganingiz uchun rahmat!', made_with: 'Fantastic Forms bilan yaratilgan',
  },
  mk: {
    submit: 'Испрати', submitting: 'Се испраќа...', next: 'Следно', previous: 'Претходно', back: 'Назад',
    required: 'Ова поле е задолжително', invalid_email: 'Внесете валидна е-пошта',
    invalid_url: 'Внесете валидна URL', invalid_phone: 'Внесете валиден телефонски број',
    loading: 'Се вчитува...', sign_above: 'Потпишете погоре', clear: 'Исчисти',
    drop_or_click_upload: 'Пуштете датотеки или кликнете за прикачување', allowed: 'Дозволено',
    max_file_size: 'Макс. големина', upload_failed: 'Прикачувањето не успеа',
    file_too_large: 'Датотеката е преголема. Макс. {size}МБ',
    file_type_not_allowed: 'Типот на датотека не е дозволен', press_enter: 'Притиснете Enter ↵',
    thank_you: 'Ви благодариме за вашето поднесување!', made_with: 'направено со Fantastic Forms',
  },
  ml: {
    submit: 'സമർപ്പിക്കുക', submitting: 'സമർപ്പിക്കുന്നു...', next: 'അടുത്തത്', previous: 'മുമ്പത്തേത്', back: 'പിന്നോട്ട്',
    required: 'ഈ ഫീൽഡ് ആവശ്യമാണ്', invalid_email: 'സാധുവായ ഇമെയിൽ നൽകുക',
    invalid_url: 'സാധുവായ URL നൽകുക', invalid_phone: 'സാധുവായ ഫോൺ നമ്പർ നൽകുക',
    loading: 'ലോഡുചെയ്യുന്നു...', sign_above: 'മുകളിൽ ഒപ്പിടുക', clear: 'മായ്ക്കുക',
    drop_or_click_upload: 'ഫയലുകൾ ഇവിടെ ഇടുക അല്ലെങ്കിൽ ക്ലിക്കുചെയ്യുക', allowed: 'അനുവദനീയം',
    max_file_size: 'പരമാവധി വലുപ്പം', upload_failed: 'അപ്‌ലോഡ് പരാജയപ്പെട്ടു',
    file_too_large: 'ഫയൽ വളരെ വലുതാണ്. പരമാവധി {size}MB',
    file_type_not_allowed: 'ഫയൽ തരം അനുവദനീയമല്ല', press_enter: 'Enter അമർത്തുക ↵',
    thank_you: 'നിങ്ങളുടെ സമർപ്പണത്തിന് നന്ദി!', made_with: 'Fantastic Forms ഉപയോഗിച്ച് നിർമ്മിച്ചത്',
  },
  ne: {
    submit: 'पेश गर्नुहोस्', submitting: 'पेश गर्दै...', next: 'अर्को', previous: 'अघिल्लो', back: 'पछाडि',
    required: 'यो फिल्ड आवश्यक छ', invalid_email: 'मान्य इमेल राख्नुहोस्',
    invalid_url: 'मान्य URL राख्नुहोस्', invalid_phone: 'मान्य फोन नम्बर राख्नुहोस्',
    loading: 'लोड हुँदै...', sign_above: 'माथि सही गर्नुहोस्', clear: 'खाली गर्नुहोस्',
    drop_or_click_upload: 'फाइलहरू यहाँ छोड्नुहोस् वा अपलोड गर्न क्लिक गर्नुहोस्', allowed: 'अनुमति',
    max_file_size: 'अधिकतम आकार', upload_failed: 'अपलोड असफल',
    file_too_large: 'फाइल धेरै ठूलो छ। अधिकतम {size}MB',
    file_type_not_allowed: 'फाइल प्रकार अनुमति छैन', press_enter: 'Enter थिच्नुहोस् ↵',
    thank_you: 'पेश गरेकोमा धन्यवाद!', made_with: 'Fantastic Forms बाट बनाइएको',
  },
  yue: {
    submit: '提交', submitting: '提交緊...', next: '下一步', previous: '上一步', back: '返回',
    required: '呢個欄位係必填', invalid_email: '請輸入有效嘅電郵',
    invalid_url: '請輸入有效嘅 URL', invalid_phone: '請輸入有效嘅電話號碼',
    loading: '載入緊...', sign_above: '喺上面簽名', clear: '清除',
    drop_or_click_upload: '將檔案拖到呢度或者按一下上傳', allowed: '允許',
    max_file_size: '檔案最大', upload_failed: '上傳失敗',
    file_too_large: '檔案太大。最大 {size}MB',
    file_type_not_allowed: '唔接受呢種檔案類型', press_enter: '撳 Enter ↵',
    thank_you: '多謝你嘅提交！', made_with: '由 Fantastic Forms 製作',
  },
};

/**
 * Build a translator bound to a language code. Falls back to English for
 * any missing key and supports a single `{size}` interpolation token.
 *
 *   const t = getTranslator(form.settings?.language);
 *   t('submit')                   // -> "Enviar"
 *   t('file_too_large', { size: 5 }) // -> "Archivo demasiado grande. Máximo 5MB"
 */
export const getTranslator = (langCode) => {
  const lang = (langCode && T[langCode]) ? langCode : 'en';
  const dict = { ...EN, ...(T[lang] || {}) };
  return (key, vars) => {
    let str = dict[key] != null ? dict[key] : (EN[key] != null ? EN[key] : key);
    if (vars) {
      Object.keys(vars).forEach((k) => {
        str = str.replace(new RegExp(`\\{${k}\\}`, 'g'), vars[k]);
      });
    }
    return str;
  };
};

export const isRtl = (langCode) => {
  const entry = LANGUAGES.find((l) => l.code === langCode);
  return !!(entry && entry.rtl);
};
